import { Controller } from "@hotwired/stimulus";
import { driver } from "driver.js";
import { getStepsForPage } from "../modules/walkthrough_steps";

/**
 * Interactive walkthrough/tutorial using Driver.js.
 * Add data-controller="walkthrough" and data-walkthrough-page-value="projects-index" to the body (or a wrapper).
 * Use data-action="click->walkthrough#start" on a button to start the tour.
 * Optional: data-walkthrough-auto-value="true" to offer the tour on first visit (uses localStorage).
 */
export default class extends Controller {
  static values = {
    page: { type: String, default: "" },
    auto: { type: Boolean, default: false },
    storageKey: { type: String, default: "walkthrough_seen" },
  };

  connect() {
    this.driver = null;
    this.pageKey = this.pageValue || this._inferPageKey();
    document.addEventListener("turbo:before-render", this._handleBeforeRender);
    this._maybeResumeTour();
  }

  disconnect() {
    document.removeEventListener("turbo:before-render", this._handleBeforeRender);
    if (this.driver) {
      this.driver.destroy();
      this.driver = null;
    }
  }

  _handleBeforeRender = () => {
    if (this.driver) {
      this.driver.destroy();
      this.driver = null;
    }
  };

  _inferPageKey() {
    const body = document.body;
    const id = body.id || "";
    const controller = id.replace(/^controller-/, "") || "";
    const actionClass = Array.from(body.classList).find((c) =>
      c.startsWith("action-")
    );
    const action = actionClass ? actionClass.replace("action-", "") : "";
    if (controller && action) return `${controller}-${action}`;
    return "";
  }

  _maybeResumeTour() {
    try {
      const next = sessionStorage.getItem("walkthrough_next");
      if (next && next === this.pageKey) {
        sessionStorage.removeItem("walkthrough_next");
        setTimeout(() => this.start(), 150);
      }
    } catch (_) {}
  }

  _clearNavHighlight() {
    document.querySelectorAll(".tour-current-nav").forEach((el) => el.classList.remove("tour-current-nav"));
  }

  _highlightCurrentNav() {
    this._clearNavHighlight();
    let selector = null;
    if (this.pageKey === "home-index") selector = ".tour-dashboard-link";
    else if (this.pageKey === "account-index") selector = ".tour-account-link";
    else if (
      this.pageKey === "projects-index" ||
      this.pageKey.startsWith("projects-") ||
      this.pageKey.startsWith("variants-")
    )
      selector = ".tour-projects-link";
    if (selector) {
      const el = document.querySelector(selector);
      if (el) el.classList.add("tour-current-nav");
    }
  }

  start() {
    const steps = getStepsForPage(this.pageKey);
    if (!steps || steps.length === 0) {
      if (typeof window.toast !== "undefined") {
        window.toast("No tour available on this page.", "info");
      } else {
        console.warn("Walkthrough: no steps defined for page", this.pageKey);
      }
      return;
    }

    if (this.driver) this.driver.destroy();

    const self = this;
    this.driver = driver({
      showProgress: true,
      steps,
      nextBtnText: "Next →",
      prevBtnText: "← Back",
      doneBtnText: "Done",
      progressText: "{{current}} of {{total}}",
      onPopoverRender: (popover, { driver }) => {
        // Position popover at bottom-left of the window
        const w = popover.wrapper;
        w.style.position = "fixed";
        w.style.left = "20px";
        w.style.bottom = "20px";
        w.style.top = "auto";
        w.style.right = "auto";
        if (popover.arrow) popover.arrow.style.display = "none";

        // Hide default close button and replace with custom <a> + SVG icon
        if (popover.closeButton) {
          popover.closeButton.style.display = "none";
        }

        if (!w.querySelector(".driver-popover-custom-close")) {
          const closeLink = document.createElement("a");
          closeLink.href = "#";
          closeLink.className = "driver-popover-custom-close";
          closeLink.setAttribute("aria-label", "Close tour");
          closeLink.innerHTML = `
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            </svg>
          `;
          closeLink.addEventListener("click", (event) => {
            event.preventDefault();
            driver.destroy();
          });
          w.appendChild(closeLink);
        }
      },
      onNextClick: (element, step, { driver: driverObj }) => {
        const idx = driverObj.getActiveIndex();
        const stepConfig = typeof idx === "number" && steps[idx] ? steps[idx] : step;
        if (stepConfig.navigateTo) {
          try {
            sessionStorage.setItem("walkthrough_next", stepConfig.nextTourPage || "");
            driverObj.destroy();
            if (window.Turbo?.visit) {
              window.Turbo.visit(stepConfig.navigateTo);
            } else {
              window.location.href = stepConfig.navigateTo;
            }
          } catch (_) {
            driverObj.moveNext();
          }
          return;
        }
        if (stepConfig.navigateToFirstProject) {
          const link = document.querySelector("#projects_list tbody tr a[href*='/projects/']");
          if (link && link.href) {
            try {
              sessionStorage.setItem("walkthrough_next", "projects-show");
              driverObj.destroy();
              if (window.Turbo?.visit) window.Turbo.visit(link.href);
              else window.location.href = link.href;
            } catch (_) {
              driverObj.moveNext();
            }
          } else {
            driverObj.moveNext();
          }
          return;
        }
        if (stepConfig.navigateToUpload) {
          const link = document.querySelector(".tour-upload-link");
          if (link && link.href) {
            try {
              sessionStorage.setItem("walkthrough_next", "projects-upload");
              driverObj.destroy();
              if (window.Turbo?.visit) window.Turbo.visit(link.href);
              else window.location.href = link.href;
            } catch (_) {
              driverObj.moveNext();
            }
          } else {
            driverObj.moveNext();
          }
          return;
        }
        if (stepConfig.navigateToProjectUrl) {
          const url = document.body.dataset.tourProjectUrl;
          if (url) {
            try {
              sessionStorage.setItem("walkthrough_next", "projects-show");
              driverObj.destroy();
              if (window.Turbo?.visit) window.Turbo.visit(url);
              else window.location.href = url;
            } catch (_) {
              driverObj.moveNext();
            }
          } else {
            driverObj.moveNext();
          }
          return;
        }
        driverObj.moveNext();
      },
      onDestroyed: () => {
        self._clearNavHighlight();
        self.driver = null;
      },
    });

    this.driver.drive();
    this._highlightCurrentNav();
    this._markSeen();
  }

  _markSeen() {
    try {
      const key = this.storageKeyValue;
      const seen = JSON.parse(localStorage.getItem(key) || "{}");
      seen[this.pageKey] = true;
      localStorage.setItem(key, JSON.stringify(seen));
    } catch (_) { }
  }

  _hasSeen() {
    try {
      const key = this.storageKeyValue;
      const seen = JSON.parse(localStorage.getItem(key) || "{}");
      return !!seen[this.pageKey];
    } catch (_) {
      return false;
    }
  }
}
